// import LocomotiveScroll from 'locomotive-scroll'
import { Raf } from '@/core/Raf'
import { View } from '@/core/View.js'
import { App } from '@/core/App.js'
import { Config } from '@/core/Config.js'
import { Device } from '@/core/Device.js'
import { Events, EventType } from '@/utils/Events.js'
import { Sleep } from '@/utils/Sleep.js'
import { Layout } from '@/core/Layout.js'
import { Nav } from '@/core/Nav.js'
import { processUrl } from '@/utils/Url.js'
import { SprigComponent } from '@/components/SprigComponent'
import { Form } from '@/components/Form'
import { Carousels_2 } from '@/components/Carousels_2'
import { FilteredListBlogTypes } from '@/components/FilteredListBlogTypes'

export class Default extends View {
  async init() {
    super.init()

    // create promise for scroll
    this.scrollPromise = new Promise((resolve) => {
      this.scrollResolve = resolve
    })

    this.onRaf = this.onRaf.bind(this)
    this.deferHandle = null
    this.lcpReadyPromise = null
    this.interactionReadyPromise = null

    this.beforeAnimateIn()

    // Replacing "#!" in links, causing "?_escaped_fragment_=" in crawlers
    document.querySelectorAll('a[href*="#!"]').forEach((link) => {
      link.setAttribute('href', link.getAttribute('href').replace('#!', '#'))
    })

    // Batch 1 — critical components needed for above-the-fold interaction
    this.initCriticalComponents().catch((error) => {
      console.warn('Failed to initialize critical components:', error)
    })

    // Batch 2 — below-the-fold and non-critical components, yielded to avoid a long task
    this.initPageComponents().catch((error) => {
      console.warn('Failed to initialize page components:', error)
    })
    this.initDeferredComponents()

    Events.on(EventType.ANIMATE_IN, this.animateIn.bind(this))
    Events.on(EventType.LOADER_DONE, this.onLoaderEnd.bind(this))
  }

  /**
   * Yield the main thread to allow the browser to paint before continuing.
   * Uses scheduler.yield() when available, falls back to setTimeout(0).
   */
  yieldToMain() {
    if ('scheduler' in window && 'yield' in window.scheduler) {
      return window.scheduler.yield()
    }
    return new Promise((resolve) => setTimeout(resolve, 0))
  }

  async addLazyComponent({
    selector,
    importer,
    exportName,
    id,
    multiple = true,
    scope = this.el,
    gate,
  }) {
    if (!this.get(selector, scope)) return false
    try {
      if (typeof gate === 'function') await gate()

      const module = await importer()
      const Component = module[exportName]

      if (!Component) {
        console.warn(`Component export "${exportName}" not found for selector "${selector}"`)
        return false
      }

      this.add(selector, Component, scope, id, multiple)
      return true
    } catch (error) {
      console.warn(`Failed to initialize component "${exportName}" for selector "${selector}"`, error)
      return false
    }
  }

  async initCriticalComponents() {
    await Promise.all([
      this.addLazyComponent({
        selector: '.c-footer',
        importer: () => import('../components/Footer.js'),
        exportName: 'Footer',
        id: 'Footer',
      }),
      this.addLazyComponent({
        selector: '[data-nav-section]',
        importer: () => import('../components/NavSection.js'),
        exportName: 'NavSection',
        id: 'NavSection',
        multiple: false,
      }),
      this.addLazyComponent({
        selector: '[data-page]',
        importer: () => import('../components/AnimateSections.js'),
        exportName: 'AnimateSections',
        id: 'AnimateSections',
      }),
      this.addLazyComponent({
        selector: '[data-scroll]',
        importer: () => import('../components/ScrollItem.js'),
        exportName: 'ScrollItem',
        id: 'ScrollItem',
      }),
      this.addLazyComponent({
        selector: '[data-obfuscated-link]',
        importer: () => import('../components/ObfuscatedLink.js'),
        exportName: 'ObfuscatedLink',
        id: 'ObfuscatedLink',
        scope: document,
      }),
      this.addLazyComponent({
        selector: '[data-modal]',
        importer: () => import('../components/Modal.js'),
        exportName: 'Modal',
        id: 'Modal',
      }),
    ])
  }

  /**
   * Initialize non-critical page components in a yielded task to avoid
   * a single long blocking task on page load.
   */
  async initPageComponents() {
    // Keep these critical features in the main bundle to avoid runtime breakage
    // when deferred chunks are stale after deploy.
    this.add('.c-sprig', SprigComponent, this.el, 'SprigComponent', true)
    this.add('.c-hubspot-form', Form, this.el, 'Form', true)
    this.add('.c-carousel--2', Carousels_2, this.el, 'Carousels_2', true)
    this.add('[data-filtered-blog-types]', FilteredListBlogTypes, this.el, 'FilteredListBlogTypes', true)

    await this.yieldToMain()

    await Promise.all([
      this.addLazyComponent({
        selector: '.c-news-widget',
        importer: () => import('../components/NewsWidget.js'),
        exportName: 'NewsWidget',
        id: 'NewsWidget',
      }),
      this.addLazyComponent({
        selector: '.c-cards-wrap',
        importer: () => import('../components/Cards_1.js'),
        exportName: 'Cards_1',
        id: 'Cards_1',
      }),
      this.addLazyComponent({
        selector: '.c-cards--5',
        importer: () => import('../components/Cards_5.js'),
        exportName: 'Cards_5',
        id: 'Cards_5',
      }),
      this.addLazyComponent({
        selector: '.c-cards--6',
        importer: () => import('../components/Cards_6.js'),
        exportName: 'Cards_6',
        id: 'Cards_6',
      }),
      this.addLazyComponent({
        selector: '.c-carousel--1',
        importer: () => import('../components/Carousels_1.js'),
        exportName: 'Carousels_1',
        id: 'Carousel_1',
      }),
      this.addLazyComponent({
        selector: '.c-carousel--7',
        importer: () => import('../components/Carousels_7.js'),
        exportName: 'Carousels_7',
        id: 'Carousel_1',
      }),
      this.addLazyComponent({
        selector: '.c-lock-scroll-1',
        importer: () => import('../components/LockScroll_1_Card.js'),
        exportName: 'LockScroll_1_Card',
        id: 'LockScroll_1_Card',
      }),
      this.addLazyComponent({
        selector: '.c-lock-scroll-1',
        importer: () => import('../components/LockScroll_1_Progress.js'),
        exportName: 'LockScroll_1_Progress',
        id: 'LockScroll_1_Progress',
      }),
      this.addLazyComponent({
        selector: '[data-accordions]',
        importer: () => import('../components/Accordions.js'),
        exportName: 'Accordions',
        id: 'Accordions',
      }),
      this.addLazyComponent({
        selector: '.c-button-readmore',
        importer: () => import('../components/ReadMoreButton.js'),
        exportName: 'ReadMoreButton',
        id: 'ReadMoreButton',
      }),
      this.addLazyComponent({
        selector: '.c-dropdown-2',
        importer: () => import('../components/Dropdown_2.js'),
        exportName: 'Dropdown_2',
        id: 'Dropdown_2',
      }),
    ])

    await this.yieldToMain()

    await Promise.all([
      this.addLazyComponent({
        selector: '.c-carousel--3',
        importer: () => import('../components/Carousels_3.js'),
        exportName: 'Carousels_3',
        id: 'Carousels_3',
      }),
      this.addLazyComponent({
        selector: '.c-carousel--6',
        importer: () => import('../components/Carousels_3.js'),
        exportName: 'Carousels_3',
        id: 'Carousels_6',
      }),
      this.addLazyComponent({
        selector: '.c-lock-scroll-3',
        importer: () => import('../components/LockScroll_3.js'),
        exportName: 'LockScroll_3',
        id: 'LockScroll_3',
      }),
      this.addLazyComponent({
        selector: '.c-section--video-inline',
        importer: () => import('../components/VideoInline.js'),
        exportName: 'VideoInline',
        id: 'VideoInline',
      }),
      this.addLazyComponent({
        selector: '.c-image',
        importer: () => import('../components/ImageComp.js'),
        exportName: 'ImageComp',
        id: 'ImageComp',
      }),
      this.addLazyComponent({
        selector: '.c-carousel--5',
        importer: () => import('../components/Carousels_5.js'),
        exportName: 'Carousels_5',
        id: 'Carousels_5',
      }),
      this.addLazyComponent({
        selector: '.c-map',
        importer: () => import('../components/Map.js'),
        exportName: 'Map',
        id: 'Map',
      }),
      this.addLazyComponent({
        selector: '[data-filtered]',
        importer: () => import('../components/FilteredList.js'),
        exportName: 'FilteredList',
        id: 'FilteredList',
      }),
      this.addLazyComponent({
        selector: '[data-filtered-blog]',
        importer: () => import('../components/FilteredListBlog.js'),
        exportName: 'FilteredListBlog',
        id: 'FilteredListBlog',
      }),
      this.addLazyComponent({
        selector: '[data-copy-to-markdown]',
        importer: () => import('../components/CopyToMarkdown.js'),
        exportName: 'CopyToMarkdown',
        id: 'CopyToMarkdown',
        multiple: false,
      }),
      this.addLazyComponent({
        selector: '[data-filtered-cases]',
        importer: () => import('../components/FilteredListCases.js'),
        exportName: 'FilteredListCases',
        id: 'FilteredListCases',
      }),
      this.addLazyComponent({
        selector: '.c-newsletter',
        importer: () => import('../components/Newsletter.js'),
        exportName: 'Newsletter',
        id: 'Newsletter',
      }),
      this.addLazyComponent({
        selector: '.c-section--title-1',
        importer: () => import('../components/SectionTitle_1.js'),
        exportName: 'SectionTitle_1',
        id: 'SectionTitle_1',
      }),
      this.addLazyComponent({
        selector: '.scrollbar',
        importer: () => import('../components/ScrollBar.js'),
        exportName: 'ScrollBar',
        id: 'ScrollBar',
      }),
      this.addLazyComponent({
        selector: '.c-event-counter',
        importer: () => import('../components/EventCounter.js'),
        exportName: 'EventCounter',
        id: 'EventCounter',
      }),
      this.addLazyComponent({
        selector: '.c-event-nav',
        importer: () => import('../components/EventNav.js'),
        exportName: 'EventNav',
        id: 'EventNav',
      }),
      this.addLazyComponent({
        selector: '.c-landing-page-nav',
        importer: () => import('../components/LandingPageNav.js'),
        exportName: 'LandingPageNav',
        id: 'LandingPageNav',
      }),
    ])

    // Hero CTA is above the fold — initialize here so the button animates in promptly
    // Skip animation on mobile to avoid hiding above-fold content during LCP
    if (this.get('.c-hero-cta', this.el)) {
      import('../components/HeroCta.js').then(({ HeroCta }) => {
        this.add('.c-hero-cta', HeroCta, this.el, 'HeroCta', true)
      })
    }
  }

  initDeferredComponents() {
    this.runWhenIdle(async () => {
      const deferredInitializers = [
        ['dev-components', () => this.initDevComponents()],
        [
          'text-components',
          async () => {
            await this.waitForLcpOrTimeout()
            await this.initTextComponents()
          },
        ],
        [
          'svg-components',
          async () => {
            await this.waitForLcpOrTimeout()
            await this.initSvgComponents()
          },
        ],
        [
          'illustration-components',
          async () => {
            await this.waitForLcpOrTimeout()
            await this.initIllustrationComponents()
          },
        ],
        [
          'interactive-components',
          async () => {
            await this.waitForFirstInteractionOrTimeout()
            await this.initInteractiveComponents()
          },
        ],
      ]

      await Promise.all(
        deferredInitializers.map(async ([label, init]) => {
          try {
            await init()
          } catch (error) {
            console.warn(`Failed to initialize ${label}:`, error)
          }
        })
      )
    }, 1200)
  }

  runWhenIdle(callback, timeout = 1500) {
    if ('requestIdleCallback' in window) {
      this.deferHandle = requestIdleCallback(() => callback(), { timeout })
      return
    }

    this.deferHandle = setTimeout(callback, 1)
  }

  waitForFirstInteractionOrTimeout(maxWait = 15000) {
    if (navigator.userActivation?.hasBeenActive) {
      return Promise.resolve()
    }

    if (this.interactionReadyPromise) return this.interactionReadyPromise

    this.interactionReadyPromise = new Promise((resolve) => {
      let done = false
      const events = ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'scroll']

      const finish = () => {
        if (done) return
        done = true
        events.forEach((event) => {
          window.removeEventListener(event, onInteract, { passive: true })
        })
        resolve()
      }

      const onInteract = () => {
        finish()
      }

      events.forEach((event) => {
        window.addEventListener(event, onInteract, { passive: true, once: true })
      })

      setTimeout(finish, maxWait)
    })

    return this.interactionReadyPromise
  }

  waitForLcpOrTimeout(maxWait = 4500) {
    if (this.lcpReadyPromise) return this.lcpReadyPromise

    this.lcpReadyPromise = new Promise((resolve) => {
      let done = false
      let observer = null

      const finish = () => {
        if (done) return
        done = true
        observer?.disconnect()
        resolve()
      }

      if (!window.performance) {
        finish()
        return
      }

      const existing = performance.getEntriesByType?.('largest-contentful-paint') || []
      if (existing.length) {
        finish()
        return
      }

      if ('PerformanceObserver' in window) {
        try {
          observer = new PerformanceObserver((list) => {
            if (list.getEntries().length) finish()
          })
          observer.observe({ type: 'largest-contentful-paint', buffered: true })
        } catch (error) {
          // Ignore unsupported observer types and fallback to timeout.
        }
      }

      setTimeout(finish, maxWait)
    })

    return this.lcpReadyPromise
  }

  async initDevComponents() {
    if (!import.meta.env.DEV) return
    if (!this.get('.js-design-toggle', this.el)) return

    const { Design } = await import('../components/dev/Design.js')
    this.add('.js-design-toggle', Design, this.el, 'DesignToggle')
  }

  async initTextComponents() {
    // Phones/tablets skip all text splitting for mobile CWV; their title highlight
    // pill is drawn statically in CSS instead (see text-styles.css).
    if (Device.MOBILE && !Device.TOUCH_DESKTOP) return
    // Touch-enabled desktops still split titles so the highlight animates as on desktop.
    const hasSplitTargets = !Device.MOBILE && this.getAll('[data-split-lines], [data-split-articles], [data-split-titles], [data-split-chars]', this.el).length > 0
    const hasTitleSpans = this.getAll('.ts-2, .ts-2--sm, .ts-3, .ts-4', this.el).length > 0

    if (hasSplitTargets) {
      const { SplitTexts } = await import('../components/SplitTexts.js')
      this.splitTexts = new SplitTexts(this.el)
    }

    if (hasTitleSpans) {
      const { TitleSpans } = await import('../components/TitleSpans.js')
      this.add('.ts-2', TitleSpans, this.el, 'TitleSpans-2', true)
      this.add('.ts-2--sm', TitleSpans, this.el, 'TitleSpans-2--sm', true)
      this.add('.ts-3', TitleSpans, this.el, 'TitleSpans-3', true)
      this.add('.ts-4', TitleSpans, this.el, 'TitleSpans-4', true)
    }
  }

  async initSvgComponents() {
    const allSvgs = this.getAll('svg', this.el)
    const idUsage = new window.Map()

    allSvgs.forEach((svg) => {
      svg.querySelectorAll('[id]').forEach((el) => {
        const id = el.getAttribute('id')
        if (!id) return
        idUsage.set(id, (idUsage.get(id) || 0) + 1)
      })
    })

    allSvgs.forEach((svg) => {
      const hasDuplicateId = Array.from(svg.querySelectorAll('[id]')).some((el) => idUsage.get(el.getAttribute('id')) > 1)
      if (hasDuplicateId) {
        svg.dataset.svgIds = 'duplicate'
      }
    })

    const targetSvgs = this.getAll('svg[data-svg-ids]', this.el)
    if (!targetSvgs.length) return

    const { Svg } = await import('../components/Svg.js')
    this.add('svg[data-svg-ids]', Svg, this.el, 'Svg', true)
  }

  async initIllustrationComponents() {
    if (Device.MOBILE) return
    const hasIllustrations =
      this.getAll('[class*="c-illustration-"]', this.el).length > 0 || this.getAll('.c-hero-science-illustration', this.el).length > 0
    if (!hasIllustrations) return

    const { ensureTheatreProject } = await import('../core/TheatreProject.js')
    await ensureTheatreProject()

    const { AnimateIllustrations } = await import('../components/AnimateIllustrations.js')
    this.add('[data-page]', AnimateIllustrations, this.el, 'AnimateIllustrations', true)
  }

  async initInteractiveComponents() {
    if (this.get('[data-video-modal]', this.el)) {
      const { VideoModal } = await import('../components/VideoModal.js')
      this.add('[data-video-modal]', VideoModal, this.el, 'VideoModal', true)
    }

    if (this.get('.c-section--video, .c-section--video-2', this.el)) {
      const { VideoSection } = await import('../components/VideoSection.js')
      this.add('.c-section--video', VideoSection, this.el, 'VideoSection', true)
      this.add('.c-section--video-2', VideoSection, this.el, 'VideoSection2', true)
    }

    if (this.get('[data-scroll-to], a[href^="#"]', this.el)) {
      const { ScrollTo } = await import('../components/ScrollTo.js')
      this.add('[data-scroll-to], a[href^="#"]', ScrollTo, this.el, 'ScrollTo', true)
    }
  }

  beforeAnimateIn() {
    // const sections = this.getAll('section, .c-section', this.el).slice(0, 2)
    // sections.map((section) => {
    //   section.style.opacity = 0
    // })
  }

  animateInParts() {
    this.components.forEach((component) => {
      if (component.animateIn) component.animateIn()
    })
  }

  resetScroll() {
    window.scrollTo(0, 0)
    document.body.scrollTop = 0
    if (history.scrollRestoration) {
      history.scrollRestoration = 'manual'
    }
  }

  async onLoaderEnd() {
    this.beforeAnimateIn()
    this.resetScroll()
    await this.initScroll()
    this.checkTheme()

    await Sleep(Config.LOADER || Config.ANIMS_ABOVE_THE_FOLD ? 500 : 0)

    this.animateInParts()
    this.animateIn()
    Nav.updateNavActive()
  }

  animateIn() {
    // this.el.style = ''
    // const sections = this.getAll('section, .c-section', this.el).slice(0, 2)
    // anime({
    //   targets: sections,
    //   delay: 0,
    //   easing: 'easeOutExpo',
    //   duration: 300,
    //   opacity: [0, 1],
    // })
  }

  async onEnter() {
    super.onEnter()
    this.resetScroll()
    await this.initScroll()
    this.checkTheme()
    this.animateInParts()
    this.animateIn()

    Nav.updateNavActive()
  }

  onLeave() {
    super.onLeave()
    this.components.forEach((component) => {
      if (component.animateOut) component.animateOut()
    })
  }

  destroy(transition, from) {
    super.destroy(transition, from)
    this.scroll?.destroy()
    Raf.remove(this.id)
    this.scrollActive = false

    if (this.deferHandle) {
      if ('cancelIdleCallback' in window) {
        cancelIdleCallback(this.deferHandle)
      } else {
        clearTimeout(this.deferHandle)
      }
      this.deferHandle = null
    }
  }

  async initScroll() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let disableSmoothScroll = prefersReducedMotion || Device.MOBILE

    try {
      if (disableSmoothScroll) {
        this.scroll = this.createNativeScrollAdapter()
      } else {
        const { default: Lenis } = await import('@studio-freight/lenis')
        this.scroll = new Lenis()
      }
    } catch (error) {
      // Keep page behavior intact when Lenis chunk/network fails.
      console.warn('Lenis failed to initialize, falling back to native scroll:', error)
      disableSmoothScroll = true
      this.scroll = this.createNativeScrollAdapter()
    }

    if (!disableSmoothScroll) {
      Raf.add(this.onRaf, this.id)
    }

    Layout.setScroll(this.scroll)
    this.scrollResolve()

    await Sleep(500)
    this.checkIfUrlHasHash()
  }

  checkIfUrlHasHash() {
    this.currentLocation = processUrl(window.location.href)
    const hash = this.currentLocation.hash
    if (this.currentLocation.hasHash) {
      this.scroll.scrollTo(hash, {
        immediate: true,
        force: true,
      })
      App.pageOverlay.hide()
    }
  }

  onRaf({ frame }) {
    this.scroll.raf(frame)
  }

  createNativeScrollAdapter() {
    const listeners = new Set()

    const getScrollTop = () => window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0

    const getScrollDimensions = () => {
      const doc = document.documentElement
      const body = document.body
      const scrollHeight = Math.max(
        doc?.scrollHeight || 0,
        body?.scrollHeight || 0,
        doc?.offsetHeight || 0,
        body?.offsetHeight || 0,
        doc?.clientHeight || 0
      )
      const limit = Math.max(scrollHeight - window.innerHeight, 0)
      return { scrollHeight, limit }
    }

    let adapter = null

    const syncState = () => {
      const scrollTop = getScrollTop()
      const { scrollHeight, limit } = getScrollDimensions()
      adapter.scroll = scrollTop
      adapter.animatedScroll = scrollTop
      adapter.limit = limit
      adapter.dimensions.scrollHeight = scrollHeight
    }

    const emitScroll = () => {
      const payload = { scroll: adapter.scroll, limit: adapter.limit }
      listeners.forEach((callback) => {
        try {
          callback(payload)
        } catch (error) {
          console.warn('Native scroll listener failed:', error)
        }
      })
    }

    const handleScroll = () => {
      syncState()
      emitScroll()
    }

    const handleResize = () => {
      syncState()
      emitScroll()
    }

    adapter = {
      options: {
        smoothWheel: false,
      },
      scroll: 0,
      animatedScroll: 0,
      limit: 0,
      dimensions: {
        scrollHeight: 0,
      },
      raf: () => {},
      destroy: () => {
        window.removeEventListener('scroll', handleScroll)
        window.removeEventListener('resize', handleResize)
        listeners.clear()
      },
      stop: () => {},
      start: () => {},
      on: (event, callback) => {
        if (event !== 'scroll' || typeof callback !== 'function') return
        listeners.add(callback)
      },
      off: (event, callback) => {
        if (event !== 'scroll' || typeof callback !== 'function') return
        listeners.delete(callback)
      },
      resize: () => {
        syncState()
        emitScroll()
      },
      scrollTo: (target, options = {}) => {
        if (target === 'top') {
          window.scrollTo({ top: 0, behavior: options.immediate ? 'auto' : 'smooth' })
          return
        }

        if (typeof target === 'string') {
          const targetEl = document.querySelector(target)
          if (!targetEl) return
          target = targetEl
        }

        if (target instanceof Element) {
          target.scrollIntoView({ behavior: options.immediate ? 'auto' : 'smooth', block: 'start' })
          return
        }

        if (typeof target === 'number') {
          window.scrollTo({ top: target, behavior: options.immediate ? 'auto' : 'smooth' })
        }
      },
    }

    syncState()

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize, { passive: true })

    return adapter
  }

  setAttribute = (attr, dataAttr, prop) => {
    const elem = this.get(`[data-${dataAttr}]`)
    document.documentElement[elem ? 'setAttribute' : 'removeAttribute'](attr, elem?.dataset[prop])
  }

  checkTheme() {
    document.documentElement.setAttribute('data-page', this.slug)

    this.setAttribute('data-theme', 'theme', 'theme')
    this.setAttribute('data-nav-theme', 'nav-theme', 'navTheme')
  }
}
